"use client";

import { AuthShell as SharedAuthShell } from "@prance/ui";
import { useRouter } from "next/navigation";
import { useLocale, useTranslations } from "next-intl";
import { type ReactNode } from "react";
import { localeNativeNames, validLocales } from "~/i18n/locales";

/**
 * ログイン画面限定のアクセントカラー(proomi/prysm/proovaのAuthShell/ACCENT定数と
 * 同じ設計 — ブランドパネル〔bg-fgの暗色〕上のロゴ・タグライン・箇条書きにのみ使う)。
 * 新しい色は起こさず、praxi既存のブランドセカンダリ(`globals.css`
 * `--prnc-secondary`、アンバー)をそのまま参照する — 暗色パネル上でのAA(4.5:1)を
 * 満たすことを確認済み。ADR-013「ブランドカラーは製品ごとに独立」により、他3製品の
 * 値と重複させない。
 */
const ACCENT = "var(--prnc-secondary)";

function useAuthLangSelector() {
  const locale = useLocale();
  const router = useRouter();

  function handleChange(next: string) {
    // biome-ignore lint/suspicious/noDocumentCookie: ui_localeはhttpOnly不要のクライアント設定値
    document.cookie = `ui_locale=${next}; path=/; max-age=${60 * 60 * 24 * 365}`;
    router.refresh();
  }

  // 絶対配置は呼び出し元(@prance/ui AuthShellのlanguageSelectスロット)が
  // top-6 right-6で行うため、ここでは配置を持たない。
  return (
    <div className="flex items-center gap-1 rounded-lg border border-disabled bg-surface px-3 py-2 text-label text-muted">
      <select
        aria-label="language"
        value={locale}
        onChange={(e) => handleChange(e.target.value)}
        className="cursor-pointer appearance-none border-none bg-transparent text-label text-muted outline-none"
      >
        {validLocales.map((l) => (
          <option key={l} value={l}>
            {localeNativeNames[l]}
          </option>
        ))}
      </select>
      <span
        aria-hidden
        className="pointer-events-none text-caption text-placeholder"
      >
        ▾
      </span>
    </div>
  );
}

type Props = { children: ReactNode };

/**
 * 2カラムの認証画面シェル(ブランドパネル + フォーム、proomi/prysm/proovaと同じ構成、
 * design.md §3.7)。フォーム側の内容(`children`)は各ページが持つ既存の状態管理・
 * `@prance/ui`の`Button`・入力欄スタイルをそのまま使う。
 *
 * レイアウト自体は @prance/ui の AuthShell(ADR-021)に移管し、ブランド要素
 * (ロゴ・タグライン・特徴文言)とフォーム本体のみこのファイルが持つ。
 */
export function AuthShell({ children }: Props) {
  const t = useTranslations("AuthPage");
  const langSelector = useAuthLangSelector();

  return (
    <SharedAuthShell
      brand={{
        logo: (
          <div
            aria-hidden
            className="size-6 shrink-0 rotate-45 rounded-[5px]"
            style={{ background: ACCENT }}
          />
        ),
        productName: "Praxi",
        tagline: t("brandTagline"),
        heading: t("brandHeadline"),
        subtext: t("brandSubtext"),
        features: [t("featureListen"), t("featureStructure"), t("featureAct")],
      }}
      accentColor={ACCENT}
      languageSelect={langSelector}
    >
      {children}
    </SharedAuthShell>
  );
}
